import type { ComponentType } from "react"
import { Head, Link } from "@inertiajs/react"
import { AdminShell } from "@/components/AdminShell"
import { PageHeader } from "@/components/PageHeader"
import { cn } from "@/lib/utils"

interface DocDef {
  slug: string
  title: string
  description: string
  content: ComponentType
}

const DOCS: DocDef[] = [
  {
    slug: "index",
    title: "Admin Docs",
    description: "Operational runbook for running Flywrite — releases, Stripe, refunds, deals, licenses, and email.",
    content: IndexDoc,
  },
  {
    slug: "local-testing",
    title: "Testing locally",
    description: "Manually exercise checkout, licensing, and email flows in the local dev environment.",
    content: LocalTestingDoc,
  },
  {
    slug: "shipping-a-release",
    title: "Shipping a release",
    description: "Build the Electron app, upload the artifacts, and publish to the auto-update feed.",
    content: ShippingAReleaseDoc,
  },
  {
    slug: "stripe-operations",
    title: "Stripe operations",
    description: "Environment setup, webhooks, and the dashboard settings that must stay on.",
    content: StripeOperationsDoc,
  },
  {
    slug: "refunds-and-cancellations",
    title: "Refunds & cancellations",
    description: "The 14-day money-back procedure and how cancellations flow through webhooks.",
    content: RefundsAndCancellationsDoc,
  },
  {
    slug: "lifetime-deals",
    title: "Lifetime deals",
    description: "How LTD checkout links, caps, and redemptions work.",
    content: LifetimeDealsDoc,
  },
  {
    slug: "licenses-and-devices",
    title: "Licenses & devices",
    description: "Key anatomy, device limits, transfer cooldowns, and when to revoke.",
    content: LicensesAndDevicesDoc,
  },
  {
    slug: "impersonating-users",
    title: "Impersonating users",
    description: "See the app as a customer to debug support issues.",
    content: ImpersonatingUsersDoc,
  },
  {
    slug: "email-and-dunning",
    title: "Email & dunning",
    description: "Transactional email setup and the failed-payment recovery flow.",
    content: EmailAndDunningDoc,
  },
]

export default function AdminDocsShow({ page }: { page: string }) {
  const doc = DOCS.find((d) => d.slug === page) ?? DOCS[0]
  const Content = doc.content

  return (
    <>
      <Head title={doc.title}>
        <meta name="description" content={doc.description} />
        <meta property="og:title" content={doc.title} />
        <meta property="og:description" content={doc.description} />
      </Head>
      <AdminShell>
        <PageHeader title={doc.title} description={doc.description} />

        <div className="mt-8 flex flex-col gap-10 lg:flex-row">
          <nav aria-label="Admin docs" className="shrink-0 lg:w-56">
            <ul className="flex flex-col gap-0.5 text-sm">
              {DOCS.map((d) => (
                <li key={d.slug}>
                  <Link
                    href={d.slug === "index" ? "/admin/docs" : `/admin/docs/${d.slug}`}
                    className={cn(
                      "block rounded-md px-2 py-1.5 no-underline transition-colors",
                      d.slug === doc.slug
                        ? "bg-accent-faded text-accent-display"
                        : "text-ink-body hover:bg-surface hover:text-ink-display",
                    )}
                  >
                    {d.slug === "index" ? "Overview" : d.title}
                  </Link>
                </li>
              ))}
            </ul>
          </nav>

          <article className="body-content min-w-0 max-w-2xl flex-1">
            <Content />
          </article>
        </div>
      </AdminShell>
    </>
  )
}

function IndexDoc() {
  return (
    <>
      <p>
        This is the internal runbook for operating Flywrite. Everything here is for admins only —
        customers never see these pages. Each guide covers one recurring operational job.
      </p>
      <h2>Guides</h2>
      <ul>
        <li>
          <Link href="/admin/docs/local-testing">Testing locally</Link> — manually exercise
          checkout, licensing, and email flows in the dev environment before shipping.
        </li>
        <li>
          <Link href="/admin/docs/shipping-a-release">Shipping a release</Link> — build the
          desktop app, upload the zip/dmg/latest-mac.yml, and publish to the update feed.
        </li>
        <li>
          <Link href="/admin/docs/stripe-operations">Stripe operations</Link> — per-environment
          setup tasks, webhook registration, and dashboard settings that must stay on.
        </li>
        <li>
          <Link href="/admin/docs/refunds-and-cancellations">Refunds & cancellations</Link> — the
          exact steps for the 14-day money-back guarantee, and how self-serve cancels behave.
        </li>
        <li>
          <Link href="/admin/docs/lifetime-deals">Lifetime deals</Link> — creating deals, sharing
          checkout URLs, and watching redemptions.
        </li>
        <li>
          <Link href="/admin/docs/licenses-and-devices">Licenses & devices</Link> — how keys work,
          device limits, and when to use the admin Revoke button.
        </li>
        <li>
          <Link href="/admin/docs/impersonating-users">Impersonating users</Link> — see the
          account area as a specific customer, then swap back.
        </li>
        <li>
          <Link href="/admin/docs/email-and-dunning">Email & dunning</Link> — transactional email
          config and the failed-payment recovery flow.
        </li>
      </ul>
      <h2>Admin area map</h2>
      <ul>
        <li>
          <Link href="/admin/accounts">Accounts</Link> — every customer account, searchable by
          name or member email. The account detail page is the hub: members, subscriptions,
          redemptions, seats, licenses, and payments in one place.
        </li>
        <li>
          <Link href="/admin/subscriptions">Subscriptions</Link> — the local mirror of Stripe
          subscriptions, filterable by status.
        </li>
        <li>
          <Link href="/admin/licenses">Licenses</Link> — look up a key someone pasted into a
          support email, see its devices, revoke it if needed.
        </li>
        <li>
          <Link href="/admin/lifetime_deals">Lifetime deals</Link> — create/edit deals and copy
          their checkout URLs.
        </li>
        <li>
          <Link href="/admin/releases">Releases</Link> — upload and publish desktop builds.
        </li>
        <li>
          <Link href="/admin/users">Users</Link> — every login across all accounts. Impersonate
          from the list or a user&apos;s detail page.
        </li>
      </ul>
    </>
  )
}

function LocalTestingDoc() {
  return (
    <>
      <h2>Setup: two terminals</h2>
      <p>
        Checkout depends on Stripe webhooks — licenses are issued when Stripe confirms payment,
        never by the checkout page itself. So local checkout testing always needs two processes:
      </p>
      <pre>
        <code>{`# terminal 1 — the app (http://localhost:3000)
bin/dev

# terminal 2 — REQUIRED: forwards Stripe test webhooks to your machine
stripe listen --api-key <sk_test key> --forward-to localhost:3000/webhooks/stripe`}</code>
      </pre>
      <p>
        The <code>stripe listen</code> signing secret is already stored in development
        credentials, so verification works out of the box. If a checkout hangs on
        &ldquo;Finalizing your purchase…&rdquo;, the webhook forwarder isn&rsquo;t running —
        that is always the cause.
      </p>
      <p>
        All emails open in the browser at <code>/letter_opener</code> — nothing is actually sent
        in development. A dev admin login exists: <code>admin@test.com</code> /{" "}
        <code>test123</code>.
      </p>

      <h2>Test a subscription purchase</h2>
      <ol>
        <li>
          Visit <code>/buy</code> (in a private window if you&rsquo;re logged in as admin).
          Change the seat quantity and confirm the tier breakdown updates live — 6 seats should
          total $564/yr.
        </li>
        <li>
          Enter a fresh email + password, continue to payment, and pay with Stripe&rsquo;s test
          card: <code>4242 4242 4242 4242</code>, any future expiry, any CVC and ZIP.
        </li>
        <li>
          The complete page polls until webhooks issue the licenses (~5–15s), then shows the
          license key. Check <code>/letter_opener</code> for the welcome email.
        </li>
        <li>
          Other useful test cards: <code>4000 0000 0000 0341</code> attaches but fails the
          charge (dunning path); <code>4000 0025 0000 3155</code> requires 3D Secure.
        </li>
      </ol>

      <h2>Test coupons and lifetime deals</h2>
      <ul>
        <li>
          <code>/buy?coupon=SAVE20</code> — prefills and auto-applies the test-mode promo (20%
          off). <code>/buy?coupon=true</code> shows an empty promo field. Plain <code>/buy</code>{" "}
          hides it entirely.
        </li>
        <li>
          <code>/buy?ldt=founders</code> — the paid lifetime deal ($49 one-time, same test
          card). <code>/buy?ldt=friends</code> — the free dev-only deal, skips payment.{" "}
          <code>/buy?ldt=anything-else</code> — the &ldquo;offer no longer available&rdquo; page.
        </li>
      </ul>

      <h2>Test licensing &amp; the Mac-app API</h2>
      <p>
        After a purchase, grab the license key from the dashboard and exercise the same endpoints
        the desktop app calls:
      </p>
      <pre>
        <code>{`# activate (device slot 1 of 3)
curl -X POST localhost:3000/api/activate -H "Content-Type: application/json" \\
  -d '{"license_key":"FW1....","machine_id":"test-1","device_name":"Test Mac"}'

# validate (use activation_token from the response)
curl -X POST localhost:3000/api/validate -H "Content-Type: application/json" \\
  -d '{"activation_token":"FWT1....","machine_id":"test-1"}'

# update feed (needs a published Release)
curl localhost:3000/api/updates/latest-mac.yml -H "X-License-Key: FW1...."`}</code>
      </pre>
      <p>
        To test lapse behavior, flip the subscription in a console —{" "}
        <code>bin/rails runner &apos;Subscription.last.update!(status: "canceled")&apos;</code> —
        then re-validate: the state becomes <code>lapsed</code> and the update feed returns 403.
        Set it back to <code>active</code> to restore.
      </p>

      <h2>Test with the real Flywrite app (recommended)</h2>
      <p>
        Instead of curl, you can point the actual desktop app at your local server and exercise
        the full activation flow exactly as customers experience it:
      </p>
      <ol>
        <li>
          Buy a license locally (any flow above) and copy the <code>FW1.…</code> key from the
          complete page or dashboard.
        </li>
        <li>
          In the flywrite repo, launch the app against your local server:
          <pre>
            <code>{`cd ~/code/active/flywrite
FLYWRITE_ACCOUNT_URL=http://localhost:3000 npm run dev`}</code>
          </pre>
        </li>
        <li>
          The activation gate appears on launch — paste the locally purchased key. Activation
          registers a device (visible under the license in this admin and on the customer&rsquo;s
          license page).
        </li>
        <li>
          From there you can test the whole lifecycle against local data: lapse the subscription
          (console command above) and click &ldquo;Check status now&rdquo; in the app&rsquo;s
          Settings → License — the app drops to read-only mode with the renew banner. Restore
          the subscription and check again — editing comes back instantly. &ldquo;Deactivate This
          Device&rdquo; returns the app to the activation gate and frees the slot.
        </li>
        <li>
          Offline grace can be simulated with{" "}
          <code>FLYWRITE_OFFLINE_GRACE_MS=60000</code> (60s) alongside the URL override.
        </li>
      </ol>
      <p>
        The <code>FLYWRITE_ACCOUNT_URL</code> override only works in unpackaged dev builds —
        packaged apps always talk to account.flywrite.app.
      </p>

      <h2>Test auto-updates end to end</h2>
      <p>
        The desktop app&rsquo;s update flow (check → download → the one-click update pill,
        including &ldquo;update when idle&rdquo; while a terminal agent is busy) can be exercised
        against the local server:
      </p>
      <ol>
        <li>
          Publish a release locally whose version is <strong>higher</strong> than the dev
          app&rsquo;s <code>package.json</code> version: build once in the flywrite repo
          (<code>npm run build:mac</code>), then upload via{" "}
          <Link href="/admin/releases/new">the upload page</Link> on localhost — or use the
          release API with the server running as{" "}
          <code>FLYWRITE_ADMIN_API_TOKEN=devtoken bin/dev</code> and the script as{" "}
          <code>FLYWRITE_ACCOUNT_URL=http://localhost:3000 FLYWRITE_ADMIN_API_TOKEN=devtoken node
          scripts/release/publish-release.mjs …</code>. (Temporarily lowering the dev app&rsquo;s{" "}
          <code>package.json</code> version is the easy way to make an existing build count as an
          update.)
        </li>
        <li>
          Launch the app with dev updates forced on, against the local server, with a locally
          activated license:
          <pre>
            <code>{`cd ~/code/active/flywrite
FLYWRITE_FORCE_DEV_UPDATES=1 FLYWRITE_ACCOUNT_URL=http://localhost:3000 npm run dev`}</code>
          </pre>
        </li>
        <li>
          The app checks ~15s after launch (or use <strong>Check for Updates…</strong> in the app
          menu). The update pill appears once the feed reports the newer version. With an idle app
          it reads &ldquo;update &amp; restart&rdquo;; start a Claude Code task in a terminal and
          it flips to &ldquo;update when idle&rdquo; — clicking then queues the install, which
          fires after all terminals have been idle for ~15s.
        </li>
      </ol>
      <p>
        <strong>Limit:</strong> dev mode verifies detection, download, and the pill/queue behavior,
        but <code>quitAndInstall</code> only genuinely swaps the app in a <strong>packaged, signed
        + notarized</strong> build (see the caveat under Shipping a release). Final verification of
        the install step means installing a signed build and publishing a newer signed release.
      </p>

      <h2>Test team, emails, and password reset</h2>
      <ul>
        <li>
          Invite a member from <code>/account/seats</code>, open the invitation email in{" "}
          <code>/letter_opener</code>, and accept it in a private window.
        </li>
        <li>
          Password reset: <code>/passwords/new</code> → submit an email → the reset link appears
          in <code>/letter_opener</code>. In production the same flow sends via Resend.
        </li>
        <li>
          Cancel/dunning emails, support form routing, and the recovery page all fire from their
          real triggers — the cancel dialog, a failed charge, and the dunning email link.
        </li>
      </ul>

      <h2>Before shipping</h2>
      <pre>
        <code>{`bin/rails test        # full suite must be green
npm run check         # TypeScript
bin/rubocop && bin/brakeman`}</code>
      </pre>
    </>
  )
}

function ShippingAReleaseDoc() {
  return (
    <>
      <h2>Recommended: the automated flow</h2>
      <p>
        In the Electron repo (<code>~/code/active/flywrite</code>), run the{" "}
        <code>release-new-version</code> Claude Code skill. It makes sure the branch is merged to
        main and pushed, bumps the version, builds the app, uploads the binaries to R2, and creates
        + publishes the release here — all through the release automation API below. The steps that
        follow describe the same flow done manually.
      </p>
      <h2>Release automation API</h2>
      <p>
        Token-authenticated JSON endpoints used by the skill (send{" "}
        <code>Authorization: Bearer &lt;token&gt;</code>):
      </p>
      <ul>
        <li>
          <code>GET /api/admin/releases/current</code> — the currently published version.
        </li>
        <li>
          <code>POST /api/admin/releases/presign_upload</code> — presigned R2 PUT URL for a binary
          (<code>version</code>, <code>filename</code>, <code>content_type</code>).
        </li>
        <li>
          <code>POST /api/admin/releases</code> — create the release from{" "}
          <code>latest_mac_yml</code> and <code>zip_key</code>/<code>dmg_key</code>; pass{" "}
          <code>publish: true</code> to go live immediately.
        </li>
      </ul>
      <p>
        The token is <code>admin_api.token</code> in Rails credentials (or the{" "}
        <code>FLYWRITE_ADMIN_API_TOKEN</code> env var on the server). Generate one with{" "}
        <code>bin/rails secret | head -c 48</code>, add it via{" "}
        <code>bin/rails credentials:edit --environment production</code>, and put the same value in{" "}
        <code>FLYWRITE_ADMIN_API_TOKEN</code> in your shell on the machine that runs the skill.
      </p>
      <h2>Release notes</h2>
      <p>
        Release notes are no longer managed here. They live as static HTML in a separate repo and
        are published at{" "}
        <a href="https://flywrite.app/release-notes">https://flywrite.app/release-notes</a>. This
        app only tracks the binaries and version — it does not store or serve notes, and the update
        feed no longer carries a <code>releaseNotes</code> field.
      </p>
      <h2>Manual flow: 1. Build in the Flywrite Electron repo</h2>
      <p>
        In <code>~/code/active/flywrite</code>, run <code>npm run build:mac</code>. electron-builder
        writes three artifacts to <code>dist/</code>:
      </p>
      <ul>
        <li>
          <code>Flywrite-&lt;version&gt;-mac.zip</code> — the archive the auto-updater downloads.
        </li>
        <li>
          <code>Flywrite-&lt;version&gt;.dmg</code> — the installer for first-time downloads.
        </li>
        <li>
          <code>latest-mac.yml</code> — the update manifest with the zip's sha512 checksum and
          size. The Rails app parses this file; you never edit it by hand.
        </li>
      </ul>
      <p>
        The version comes from the Electron repo's <code>package.json</code> — bump it there before
        building.
      </p>
      <h2>2. Upload via Releases → Upload release</h2>
      <p>
        On <Link href="/admin/releases/new">the upload page</Link>, enter the version (must match
        the build) and attach the zip, the dmg, and <code>latest-mac.yml</code>. The browser
        uploads the binaries directly to R2 under{" "}
        <code>releases/&lt;version&gt;/</code> via presigned URLs — they never pass through the
        Rails server. The release is created as a <strong>draft</strong>: nothing is customer
        visible yet.
      </p>
      <h2>3. Publish</h2>
      <p>
        On <Link href="/admin/releases">the releases list</Link>, hit <strong>Publish</strong>.
        That stamps <code>released_at</code> and makes it the current release. From that moment:
      </p>
      <ul>
        <li>
          The update feed at <code>/api/updates/latest-mac.yml</code> serves this version. The feed
          is gated: the desktop app must present a license key, and only <strong>active</strong> or{" "}
          <strong>grace</strong> licenses get the manifest — lapsed/revoked licenses get nothing,
          so they stop receiving updates.
        </li>
        <li>
          Download links (from emails and the account area) serve the new dmg/zip via short-lived
          presigned R2 URLs.
        </li>
      </ul>
      <h2>Storage</h2>
      <p>
        Binaries live in the Cloudflare R2 bucket (separate dev and production buckets, configured
        in Rails credentials under <code>cloudflare_r2</code>). Keys are{" "}
        <code>releases/&lt;version&gt;/&lt;filename&gt;</code>.
      </p>
      <h2>Signing + notarization</h2>
      <p>
        Since August 2026, releases are <strong>signed with the CasJam Media LLC Developer ID and
        notarized by Apple</strong> (the Electron repo's <code>build:mac:release</code> script
        handles both). Downloads open without Gatekeeper warnings and auto-updates install end to
        end. Only publish builds produced by <code>build:mac:release</code> — the plain{" "}
        <code>build:mac</code> output is unsigned and must never ship to customers.
      </p>
    </>
  )
}

function StripeOperationsDoc() {
  return (
    <>
      <h2>Environments</h2>
      <p>
        Development and staging use Stripe <strong>test mode</strong> keys (stored in the
        development/staging Rails credentials); production uses <strong>live mode</strong> keys in
        the production credentials. Each environment needs its own one-time setup:
      </p>
      <ol>
        <li>
          <code>bin/rails stripe:setup</code> — creates the Flywrite product and the tiered
          graduated monthly price in that Stripe account.
        </li>
        <li>
          <code>bin/rails stripe:register_webhook</code> — registers the webhook endpoint for that
          environment's host and prints the signing secret.
        </li>
        <li>
          Put the printed signing secret into that environment's credentials as{" "}
          <code>stripe.webhook_secret</code> (via{" "}
          <code>bin/rails credentials:edit --environment production</code> etc.).
        </li>
      </ol>
      <h2>Local webhooks in development</h2>
      <p>Use the Stripe CLI instead of a registered endpoint:</p>
      <ul>
        <li>
          <code>stripe listen --forward-to localhost:3000/webhooks/stripe</code>
        </li>
        <li>
          Put the <code>whsec_…</code> secret it prints into the development credentials'{" "}
          <code>stripe.webhook_secret</code>.
        </li>
      </ul>
      <h2>Dashboard settings to verify (both test and live)</h2>
      <ul>
        <li>
          <strong>Smart Retries: ON</strong> (Settings → Billing → Revenue recovery). Stripe
          retries failed charges over ~2 weeks; each failure triggers our dunning email.
        </li>
        <li>
          <strong>Upcoming renewal events</strong>: set the <code>invoice.upcoming</code> lead time
          to <strong>30 days</strong> (Settings → Billing → Subscriptions and emails). This drives
          the "your renewal is coming" notice and pending seat-decrease application.
        </li>
      </ul>
      <h2>What the webhooks do</h2>
      <p>
        All state flows one way: Stripe → webhook → local mirror. Subscription create/update/delete
        maintain the <code>subscriptions</code> table and seat counts; <code>invoice.paid</code>{" "}
        records payments and restores past-due licenses; failed payments send dunning emails;{" "}
        <code>customer.subscription.deleted</code> revokes the account's subscription licenses
        immediately. Events are stored in <code>webhook_events</code> with dedupe on the Stripe
        event id — safe to replay from the Stripe dashboard if something was missed.
      </p>
    </>
  )
}

function RefundsAndCancellationsDoc() {
  return (
    <>
      <h2>14-day money-back guarantee (manual, in Stripe dashboard)</h2>
      <p>When a customer asks for their money back within 14 days, do both steps, in Stripe:</p>
      <ol>
        <li>
          <strong>Refund the payment</strong> — find the payment/invoice on the customer and refund
          it in full.
        </li>
        <li>
          <strong>Cancel the subscription immediately</strong> — cancel it with "Cancel
          immediately", <em>not</em> "at period end". The immediate cancel fires{" "}
          <code>customer.subscription.deleted</code>, and that webhook revokes the account's
          licenses instantly. If you cancel at period end instead, the customer keeps a working
          license until the period ends despite being refunded.
        </li>
      </ol>
      <p>
        Nothing needs to be done in this admin area — the webhook handles license revocation. Use
        the account page afterwards to confirm the licenses show as lapsed/revoked.
      </p>
      <h2>Self-serve cancellations</h2>
      <p>
        Customers cancel from Account → Billing. That schedules the cancel{" "}
        <strong>at period end</strong>: they keep access until the paid period runs out, then the{" "}
        <code>subscription.deleted</code> webhook lapses the licenses. They can resume any time
        before the period ends.
      </p>
      <h2>Cancellation reasons</h2>
      <p>
        The cancel flow asks why they're leaving; responses are emailed to{" "}
        <strong>info@flywrite.app</strong>.
      </p>
      <h2>Lifetime deal refunds</h2>
      <p>
        LTD purchases are one-off payment intents. Refund the payment in Stripe, then use the
        admin <strong>Revoke</strong> button on the account's license — there's no subscription
        webhook to do it for you.
      </p>
      <p>
        Mimeo customer status follows usable licenses, not the refund itself. Final subscription
        cancellation or admin license revocation changes the owner to <code>cancelled</code> only
        when no other usable license remains. A valid lifetime license keeps the owner active.
        Historical customer tags and email preferences are not removed or changed.
      </p>
    </>
  )
}

function LifetimeDealsDoc() {
  return (
    <>
      <h2>How a deal works</h2>
      <ul>
        <li>
          Each deal has a <strong>slug</strong>; its checkout URL is{" "}
          <code>account.flywrite.app/buy?ldt=slug</code>. Copy it straight from{" "}
          <Link href="/admin/lifetime_deals">the deals list</Link> and share it anywhere (launch
          emails, communities, partner drops).
        </li>
        <li>
          A deal has a one-time <strong>price</strong> (or $0 for a free giveaway deal — free deals
          skip payment entirely at checkout).
        </li>
        <li>
          Completed paid and free deals both count as active Flywrite customers in Mimeo while
          their license is usable, and receive the lifetime-deal identity tag and deal name.
          The paying-customer tag requires an actual positive payment; a free grant alone does
          not qualify. Neither grants a newsletter preference.
        </li>
        <li>
          Supply caps: <strong>max redemptions</strong> and/or an <strong>expiry date</strong>,
          both optional. A deal is redeemable while it's active, unexpired, and not sold out.
        </li>
        <li>
          Redemption is <strong>atomic</strong> — a row lock plus a DB check constraint make
          overselling impossible even if two buyers hit the last unit simultaneously. Paid
          checkouts reserve a unit as <em>pending</em> and complete on payment; abandoned
          reservations expire and free the unit.
        </li>
        <li>
          One redemption per account. A completed redemption grants one seat with a{" "}
          <strong>lifetime license</strong> (always active, never billed).
        </li>
      </ul>
      <h2>The Founding Users launch deal</h2>
      <p>
        The launch deal is <strong>Founding Users</strong>: $49, capped at 50 redemptions, expires
        October 1, 2026. Watch it fill from its{" "}
        <Link href="/admin/lifetime_deals">deal card</Link> or the per-deal Redemptions page.
      </p>
      <h2>Managing deals</h2>
      <ul>
        <li>
          <strong>Deactivate</strong> (uncheck Active) to stop new redemptions instantly without
          affecting existing customers.
        </li>
        <li>
          <strong>Delete</strong> only works on deals with zero redemptions — once anyone has
          redeemed, the deal is part of their purchase history; deactivate instead.
        </li>
        <li>
          The <strong>Redemptions</strong> view shows who redeemed, when, for how much, and links
          to each account.
        </li>
      </ul>
    </>
  )
}

function LicensesAndDevicesDoc() {
  return (
    <>
      <h2>Key anatomy</h2>
      <p>
        A license key is an <strong>Ed25519-signed token</strong> that embeds the license id and
        account id. The desktop app verifies the signature offline with the public key; the private
        signing key lives only in Rails credentials. Keys store <strong>no expiry</strong> — a
        key's status is derived at validation time from what backs its seat:
      </p>
      <ul>
        <li>
          <strong>Lifetime deal seat</strong> → active forever (as long as the redemption is
          completed).
        </li>
        <li>
          <strong>Subscription seat</strong> → follows Stripe: active subscription → active;
          past_due → <em>grace</em> (still works, during dunning); canceled/unpaid → lapsed.
        </li>
        <li>
          <strong>Revoked</strong> (admin action or immediate cancel) always wins over everything.
        </li>
      </ul>
      <p>
        Because status is derived, there's nothing to "extend" or "renew" on a key — fixing the
        subscription fixes the key.
      </p>
      <h2>Devices</h2>
      <ul>
        <li>Each license activates on up to <strong>3 devices</strong>.</li>
        <li>
          Deactivating a device frees a slot, but the same machine can only be deactivated once per{" "}
          <strong>30 days</strong> per license — a cooldown that deters key-sharing by slot
          cycling.
        </li>
        <li>
          The license detail page here shows every device (name, machine id, activation dates), so
          you can see exactly where a key is in use.
        </li>
      </ul>
      <h2>When to use admin Revoke</h2>
      <p>
        Rarely. Refunds and cancellations are handled by Stripe webhooks — you should almost never
        revoke for billing reasons (exception: lifetime deal refunds, which have no webhook).
        Revoke is for <strong>abuse</strong>: a leaked/shared key, a chargeback-and-keep-using
        situation, etc. Revocation is immediate; devices fail their next validation.
      </p>
      <h2>Undoing a revoke (Reactivate)</h2>
      <p>
        A revoked license shows a <strong>Reactivate</strong> button on its detail page. This is
        for rare cases only — a mistaken revoke or some other special circumstance. It clears the
        revocation, after which the key's status re-derives from its billing source as usual (so a
        canceled subscription still reads lapsed — reactivating doesn't grant access the billing
        state wouldn't). Devices pick the change up on their next validation, or immediately if the
        user hits the "I've reactivated" button in the app's license banner.
      </p>
      <h2>How customers see keys</h2>
      <p>
        Account owners manage seats on Account → Seats & Team and see full keys there. A member
        assigned to a seat sees their own key on Account → License. The desktop app activates by
        pasting the key (or via the sign-in flow), and validates it periodically against{" "}
        <code>/api/validate</code>.
      </p>
    </>
  )
}

function ImpersonatingUsersDoc() {
  return (
    <>
      <h2>When to use it</h2>
      <p>
        Impersonation lets an admin see the account area exactly as a customer does — their
        license page, seats, billing, or a support issue you can&apos;t reproduce from admin.
        Only admins can start it.
      </p>
      <h2>How it works</h2>
      <p>
        The same session row stays in place. Starting impersonation swaps{" "}
        <code>sessions.user_id</code> to the target user and stores the admin&apos;s id on{" "}
        <code>sessions.impersonating_user_id</code>. Stopping swaps them back. Pages always render
        as the effective user; the true admin is kept so <code>/admin/*</code> (including Stop)
        still works while you&apos;re viewing a non-admin.
      </p>
      <h2>How to start</h2>
      <ol>
        <li>
          Open <Link href="/admin/users">Users</Link> and click <strong>Impersonate</strong> on
          the row, or open the person and click Impersonate there.
        </li>
        <li>
          You land on their License page. An amber bar at the top of every signed-in page reads{" "}
          <em>Impersonating &lt;customer&gt; as &lt;you&gt;</em>.
        </li>
      </ol>
      <h2>How to stop</h2>
      <p>
        Click <strong>Stop</strong> in the banner (or <strong>Stop impersonating</strong> on that
        user&apos;s admin page). You return to their admin user page as yourself.
      </p>
      <h2>Rules</h2>
      <ul>
        <li>Only admins can start impersonation.</li>
        <li>You can&apos;t impersonate yourself.</li>
        <li>
          Actions you take while impersonating are real — they happen as that user. Don&apos;t
          change seats, billing, or devices unless you mean to.
        </li>
        <li>
          Signing out ends the session entirely (including the stored admin id). You&apos;ll need
          to log in again.
        </li>
      </ul>
    </>
  )
}

function EmailAndDunningDoc() {
  return (
    <>
      <h2>Mimeo customer audiences</h2>
      <p>
        Account owners/purchasers sync through <code>flywrite_customer_updated</code> with product{" "}
        <code>flywrite</code>. <code>flywrite_status</code> is <code>active</code> while any owned
        account has a usable license (including billing grace and free lifetime deals), otherwise{" "}
        <code>cancelled</code>. Invited members and unfinished registrations are not customer
        enrollments. These statuses describe access, not marketing consent or email unsubscribe.
      </p>
      <p>
        Mimeo&apos;s broad Flywrite audience includes customer statuses and existing Flywrite
        opt-ins. Active Flywrite Customers accepts <code>active</code> and legacy <code>paying</code>;
        Cancelled Flywrite Customers accepts <code>cancelled</code>. Paying and lifetime tags are
        historical identity facts. No Builder Briefing/BMP membership, update preference,
        suppression, or reserved subscription event is changed by this integration.
      </p>
      <h3>Delivery and reconciliation</h3>
      <p>
        Set server-only <code>MIMEO_API_TOKEN</code> or Rails credentials <code>mimeo.api_token</code>.
        Solid Queue delivers durable snapshots with stable retry keys; failed attempted deliveries
        hold later changes until resolved. <code>bin/rails mimeo:status</code> shows aggregate
        failures; <code>bin/rails mimeo:reconcile</code> previews customer counts.
        Applying reconciliation or retrying production events requires separate approval. See{" "}
        <code>docs/mimeo-customer-sync.md</code> in the repository for the release and recovery runbook.
      </p>
      <p>
        Customer events use the owner's current email address when captured. They do not
        require source identity linking or mailbox verification. Historical identity tables
        and metadata remain in the database but are no longer used by the account workflow.
        Obsolete pre-cutover jobs are no longer supported. Inspect and reconcile any queued or
        failed legacy jobs before deploying; production cleanup requires separate approval.
      </p>
      <p>
        An email edit sends a 20-minute, single-use authorization to the current mailbox.
        Opening the link only shows the proposed change; a signed-in confirmation submits it.
        Local and delivery-address conflicts are checked before sending and again on confirmation.
        The same existing Mimeo person is renamed before saving the Flywrite login; a missing
        person is skipped. Failures stop for support without automatic retries or merges.
        Signup, checkout and license access do not require email verification.
      </p>
      <p>
        After success, existing Stripe customers for owned accounts receive the new email on a
        best-effort basis. Members and admin-nonowners do not change billing. Stripe errors are
        logged without undoing the change. A completion notice goes to the old mailbox.
        Deploy the matching Mimeo email API first and inspect old queued identity jobs before
        replacing workers. Keep existing integration tokens and original scoped event keys;
        never replay them under an operator token. Follow the repository runbook for rollout.
      </p>
      <h2>Transactional email</h2>
      <ul>
        <li>
          <strong>Production</strong> sends via <strong>Resend</strong> from{" "}
          <code>no-reply@account.flywrite.app</code> with reply-to{" "}
          <code>support@flywrite.app</code>. The Resend API key lives in production credentials.
        </li>
        <li>
          <strong>Development</strong> uses letter_opener — every outgoing email is viewable at{" "}
          <code>/letter_opener</code>, nothing actually sends.
        </li>
      </ul>
      <h2>New-purchase notifications</h2>
      <p>
        Every completed purchase — an annual subscription (first{" "}
        <code>invoice.paid</code> with <code>subscription_create</code>) or a lifetime deal (on{" "}
        <code>FinalizeRedemption</code>) — sends an internal notification to{" "}
        <code>brian@flywrite.app</code> (<code>PurchaseMailer#new_purchase</code>). Subject:{" "}
        <code>[Flywrite purchase] &lt;buyer email&gt;</code>. Body lists the buyer email, the
        price (<code>$99/year</code> or <code>$49 lifetime deal (deal name)</code>), and deep
        links to the Stripe customer and the admin account / user / license pages. The Stripe
        link points at the <strong>test</strong> or <strong>live</strong> dashboard depending on
        the active API key, and is omitted when the account has no Stripe customer id (e.g. a free
        lifetime deal).
      </p>
      <h2>Dunning (failed payments)</h2>
      <p>The whole flow is automatic:</p>
      <ol>
        <li>
          A renewal charge fails → Stripe Smart Retries kick in, and the subscription goes{" "}
          <code>past_due</code> (licenses drop to <em>grace</em> — the app keeps working).
        </li>
        <li>
          <strong>Every failed charge</strong> triggers an email to the owner with a{" "}
          <strong>signed recovery link valid for 14 days</strong>.
        </li>
        <li>
          The link opens a <strong>no-login recovery page</strong> where they enter a new card —
          no password reset dance while their payment is broken.
        </li>
        <li>
          When the retry succeeds, the <code>invoice.paid</code> webhook restores the subscription
          to active (licenses back to active) and sends a confirmation email.
        </li>
        <li>
          If Stripe exhausts retries, the subscription goes unpaid/canceled and licenses lapse.
        </li>
      </ol>
      <h2>Support form</h2>
      <p>
        Account → Support hosts the customer support form; submissions are emailed to{" "}
        <strong>support@flywrite.app</strong>. It's <strong>license-gated</strong>: only accounts
        holding a currently valid (active or grace) license can submit, which keeps the queue to
        actual customers.
      </p>
      <h2>Other automated emails</h2>
      <ul>
        <li>Purchase receipt + license key email after checkout.</li>
        <li>Team invitation emails (accept link).</li>
        <li>Renewal-coming-up notice (from <code>invoice.upcoming</code>, ~30 days out).</li>
        <li>Cancellation-reason notifications to <strong>info@flywrite.app</strong>.</li>
      </ul>
    </>
  )
}
